import React, { useMemo, useState } from 'react';
import {
    Table,
    Typography,
    Empty,
    Space,
    Avatar,
    Tooltip,
    Input,
    Spin,
    Pagination,
    Row,
    theme,
    Menu,
    message,
    Dropdown,
} from 'antd';
import { DownloadOutlined, EllipsisOutlined, EyeOutlined, MoreOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { reportsActions } from '../../reports/reportsTimeSheet/redux/slice';
import { setLimit, setSearch, setSkip } from '../store/projectReportSlice';
import { debounce } from 'lodash';
import { teamListGet, teamState } from '../../team/team.slice';
import dayjs from 'dayjs'

const { Text } = Typography;

export default function SaveProjectListTable({ setShowWorkReport, showWorkReport, setReportId }) {
    const { useToken } = theme
    const { token } = useToken()
    const { reports, isLoading, skip, limit, count, isNext } = useSelector((state) => state.projectReportSlice);
    const [loadingReportId, setLoadingReportId] = useState(null);
    const [loadingAction, setLoadingAction] = useState(null)
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const dispatch = useDispatch()

    // ✅ Extract date range from reportName
    const extractDateRange = (reportName) => {
        if (!reportName) return "-";
        const match = reportName.match(/(\d{2}-\d{2}-\d{4})\s+to\s+(\d{2}-\d{2}-\d{4})/);
        if (match) {
            return `${match[1]} → ${match[2]}`;
        }
        return "-";
    };


    const {
        teamListLoading,
    } = useSelector(teamState)



    const rows = useMemo(() => {
        if (!Array.isArray(reports)) return [];
        return reports.map((report) => {
            const reportName = report?.reportName || "-";
            const reportDate = extractDateRange(reportName);
            const userName = report?.userId?.name || "-";
            const userPic = report?.userId?.profilePicture || null;
            const reportData = report?.reportData;
            const assignee = report?.assigneeId;
            const project = report?.projectId;
            const start = report?.start
            const end = report?.end
            const _id = report?._id;
            const createAt = report?.createdAt
            const reportUrl = report?.reportUrl

            return {
                key: report._id,
                reportName,
                reportDate,
                createdBy: userName,
                userPic,
                reportData,
                assignee,
                project,
                start,
                end,
                _id,
                createAt,
                reportUrl
            };
        });
    }, [reports]);

    const pagedRows = useMemo(() => {
        const startIndex = (page - 1) * limit;
        return rows.slice(startIndex, startIndex + limit);
    }, [rows, page, limit]);

    const columns = [
        {
            title: 'Report Name',
            dataIndex: 'reportName',
            key: 'reportName',
            render: (val) => <Text strong>{val}</Text>,
        },
        {
            title: 'Report Date',
            dataIndex: 'reportDate',
            key: 'reportDate',
            render: (val) => <Text>{val}</Text>,
        },
        {
            title: 'Report Generate Date',
            dataIndex: 'createAt',
            key: 'reportDate',
            render: (val) => <Text>{dayjs(val).format("DD-MM-YYYY hh:mm A")}</Text>,
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (val, row) => (
                <Space>
                    {/* {row.userPic ? (
                        <Avatar src={row.userPic} />
                    ) : (
                        <Avatar>{val?.[0]}</Avatar>
                    )} */}
                    <Text>{val}</Text>
                </Space>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            render: (val, row) => {
                const handleMenuClick = async (key) => {
                    setLoadingReportId(val._id); // row-wise loading
                    setLoadingAction(key);       // action-wise loading

                    try {
                        if (key === 'view') {
                            const { payload } = await dispatch(
                                teamListGet({
                                    skip: 0,
                                    limit: 200,
                                    projectId: val?.project?._id,
                                })
                            );

                            const teamData = payload?.data || [];
                            const enrichedAssignees = val?.assignee?.map((user) => {
                                const match = teamData.find((t) => t._id === user._id);
                                return {
                                    label: user?.name,
                                    value: user?._id,
                                    profilePicture: user?.profilePicture,
                                    designation: match?.designation || null,
                                };
                            });

                            dispatch(reportsActions.setLoading(false));
                            dispatch(
                                reportsActions.downloadPdfData({
                                    pdfData: JSON.parse(val?.reportData),
                                    pdfSelectedData: {
                                        assignees: enrichedAssignees,
                                        start: val?.start,
                                        end: val?.end,
                                        project: {
                                            id: val?.project?._id,
                                            name: val?.project?.name,
                                        },
                                    },
                                })
                            );

                            setShowWorkReport(true);
                            setReportId(val._id);
                        }
                        else if (key === 'download') {
                            if (val?.reportUrl) {
                                try {
                                    const response = await fetch(val.reportUrl, { method: "GET" });
                                    const blob = await response.blob();

                                    const link = document.createElement("a");
                                    link.href = URL.createObjectURL(blob);
                                    link.download = val?.reportName || `report-${val?._id}.pdf`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);

                                    URL.revokeObjectURL(link.href);
                                    // message.success("Download started");
                                } catch (err) {
                                    console.error(err);
                                    message.error("Failed to download report");
                                }
                            } else {
                                message.warning("This report is an old version and cannot be downloaded.");
                            }
                        }
                    } finally {
                        // reset loading
                        setLoadingReportId(null);
                        setLoadingAction(null);
                    }
                };

                const menu = (
                    <Menu onClick={(e) => handleMenuClick(e.key)}>
                        <Menu.Item key="view" icon={<EyeOutlined />} disabled={loadingReportId === val._id}>
                            {loadingReportId === val._id && loadingAction === "view" ? "Loading..." : "View"}
                        </Menu.Item>
                        <Menu.Item key="download" icon={<DownloadOutlined />} disabled={loadingReportId === val._id}>
                            {loadingReportId === val._id && loadingAction === "download" ? "Downloading..." : "Download"}
                        </Menu.Item>
                    </Menu>
                );

                return (
                    <Dropdown overlay={menu} trigger={['click']}>
                        {loadingReportId === val._id ? (
                            <Spin size="small" />
                        ) : (
                            <MoreOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
                        )}
                    </Dropdown>
                );
            },
        }

    ];

    const searchFn = debounce(async (search) => {
        dispatch(setSearch(search))
    }, 800)

    return (
        <div >
            <Input
                style={{
                    height: '44px',
                    width: "300px",
                    marginBottom: "15px"
                }}
                placeholder="Search"
                onChange={(event) => searchFn(event.target.value)}
                prefix={
                    <SearchOutlined
                        style={{
                            fontSize: 16,
                            marginRight: 5,
                        }}
                    />
                }
            />

            <Spin spinning={isLoading} tip="Loading reports...">

                <Table
                    style={{
                        borderRadius: '16px',
                    }}
                    rootClassName="tableGlobal"
                    columns={columns}
                    dataSource={pagedRows}
                    rowKey={(r) => r.key}
                    bordered
                    size="middle"
                    locale={{ emptyText: <Empty description="No reports found" /> }}
                    pagination={false}
                />
                <Row
                    justify={'end'}
                    align={'middle'}
                    style={{
                        border: `2px solid ${token.colorPalette.baseColor.quaternary}`,
                        padding: '20px',
                        borderRadius: '0px 0px 8px 8px',
                    }}
                >
                    <Text>
                        {reports?.length} - {limit} of {count}
                    </Text>

                    <Pagination
                        defaultCurrent={skip / limit + 1}
                        // defaultPageSize={limit}
                        pageSizeOptions={[5, 10, 20, 50]}
                        showSizeChanger={true}
                        total={
                            isNext === false &&
                                // filterView &&
                                skip / limit + 1 === 1
                                ? reports?.length
                                : count
                        }
                        onChange={async (value, pageSize) => {

                            const newSkip = (value - 1) * pageSize;
                            dispatch(setSkip(newSkip));
                            dispatch(setLimit(pageSize));
                        }}
                    />
                </Row>
            </Spin>

        </div>
    );
}
